import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { googleAuthSession, resetAuthUser } from "../lib/api";

/**
 * Emergent Google Auth callback. The auth provider redirects back to
 * `<origin>/<path>#session_id=<id>`. App.js synchronously detects the fragment
 * and renders this component, which exchanges the one-time session_id for a
 * session cookie, then routes by intent:
 *   - /admin   → admin dashboard
 *   - /build   → resume the "build for agent/human" flow
 *   - anything → back to the homepage
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const path = window.location.pathname || "/";
    const isAdmin = path.startsWith("/admin");
    const isBuild = path.startsWith("/build");
    const isInstall = path.startsWith("/install");
    const search = window.location.search || "";
    const failDest = isAdmin ? "/admin/login" : "/";
    // Default back to the exact path (+ query) the auth landed on — e.g. a
    // /claim/:slug page — so users return to where they started instead of home.
    const okDest = isAdmin ? "/admin" : isBuild ? "/build" : isInstall ? "/install" : (path + search);

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    (async () => {
      if (!sessionId) {
        nav(failDest, { replace: true });
        return;
      }
      try {
        await googleAuthSession(sessionId);
        // Clear the shared /auth/me cache and HARD-reload to the destination so
        // the whole app re-initialises WITH the freshly-set session cookie. A
        // client-side nav would keep the stale "logged out" cache that any
        // always-mounted component populated during this callback page-load.
        resetAuthUser();
        window.location.replace(okDest);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Google sign-in failed";
        toast.error(msg);
        window.history.replaceState(null, "", failDest);
        nav(failDest, { replace: true });
      }
    })();
  }, [nav]);

  return (
    <div
      data-testid="auth-callback"
      style={{ fontFamily: "'IBM Plex Mono', monospace", padding: 24, fontSize: 13 }}
    >
      {">"}_ authenticating…
    </div>
  );
}
